<script setup lang="ts">
import Header4 from '@/components/base/typography/Header4.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import OnsiteOfferQuizIcon from '@/components/quiz/OnsiteOfferQuizIcon.vue';
import OnsiteOfferQuizValueProp from '@/components/quiz/OnsiteOfferQuizValueProp.vue';
import QuizAnswer from '@/components/quiz/QuizAnswer.vue';
import QuizQuestion from '@/components/quiz/QuizQuestion.vue';
import QuizWrapper, { KeyedAnswers } from '@/components/quiz/QuizWrapper.vue';
import { useOnsiteOffer } from '@/composables/personalization/useOnsiteOffer';
import { ImpactfulQuizQuestion, isImpactfulQuizQuestion, questions } from '@/data/quiz/onsiteOffer';
import { isDefined } from '@/utils/isDefined';

const emit = defineEmits<{
  'quiz-completed': [];
}>();

const { results } = useOnsiteOffer();

function filterImpactfulAnswers(question: ImpactfulQuizQuestion, quizResults: KeyedAnswers) {
  const selectedAnswerIds = quizResults[question.id].map((answer) => answer.id);
  const answers = question.answers.filter((answer) => selectedAnswerIds.includes(answer.id));
  return {
    ...question,
    answers,
  };
}

function handleQuizCompleted(quizResults: KeyedAnswers) {
  const [firstQuestion, secondQuestion] = questions.filter(isImpactfulQuizQuestion);
  results.value.selections = [
    filterImpactfulAnswers(firstQuestion, quizResults),
    filterImpactfulAnswers(secondQuestion, quizResults),
  ];

  emit('quiz-completed');
}

function selectionInstructions(maxSelections?: number) {
  if (!isDefined(maxSelections)) return 'Select all that apply';
  if (maxSelections > 1) return `Select up to ${maxSelections}`;
  return 'Select one';
}

const fullGridWidthByColumn = {
  1: 'col-span-1',
  2: 'col-span-2',
};
const gridClassesByColumn = {
  1: 'flex flex-col',
  2: 'grid grid-cols-2 items-start',
};
</script>

<template>
  <QuizWrapper
    class="pb-44 md:pb-40"
    hideBackButtonFirstQuestion
    :questions
    @quiz-completed="handleQuizCompleted"
    v-slot="{ currentQuestion, handleAnswerSelected, selectedAnswers }"
  >
    <QuizQuestion
      :id="currentQuestion.id"
      :maxSelections="currentQuestion.maxSelections"
      :selectedAnswers
      :text="currentQuestion.title"
      @select-answer="handleAnswerSelected"
    >
      <template #question-header>
        <div class="flex items-center justify-center gap-2">
          <Header4 class="w-full text-center custom-gelica">
            {{ currentQuestion.title }}
          </Header4>
        </div>

        <SmallBodyText class="w-full text-center mt-2.5">
          {{ selectionInstructions(currentQuestion.maxSelections) }}
        </SmallBodyText>
      </template>

      <template v-slot="{ handleSelectAnswer, selectedAnswersForQuestion }">
        <div class="flex flex-col items-center w-full">
          <div class="w-full gap-3 sm:w-2/3" :class="gridClassesByColumn[currentQuestion.columns]">
            <QuizAnswer
              v-for="answer in currentQuestion.answers"
              :class="{
                [fullGridWidthByColumn[currentQuestion.columns]]:
                  currentQuestion.columns > 1 &&
                  answer === currentQuestion.answers.at(-1) &&
                  currentQuestion.answers.length % currentQuestion.columns !== 0,
              }"
              :key="answer.id"
              :id="answer.id"
              :questionId="currentQuestion.id"
              :selected="selectedAnswersForQuestion.includes(answer.id)"
              @select-answer="handleSelectAnswer"
            >
              <OnsiteOfferQuizIcon class="block mr-2.5" :quizAnswerId="answer.id" />
              <SmallBodyText>
                {{ answer.answerText }}
              </SmallBodyText>
            </QuizAnswer>
          </div>
        </div>
        <OnsiteOfferQuizValueProp
          :answers="currentQuestion.answers"
          class="absolute left-0 right-0 bottom-[74px] bg-nuts-orange-100"
          :selectedAnswersForQuestion
        />
      </template>
    </QuizQuestion>
  </QuizWrapper>
</template>

<style scoped>
.custom-gelica {
  @apply font-gelica;
}
</style>
